import { CircleHelp } from "lucide-react";
import React from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgSelect } from "@intelligems/ig-theme/IgSelect";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { OnboardingInfoFe } from "@intelligems/ig-types/fe-only/onboarding";
import { ListItemButton, MenuItem, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import { getMixpanelStepTitle } from "@src/components/MainApp/Onboarding/utils";
import { useViewPage } from "@src/hooks/mixpanel";
import { plusOnboardingBookDemoLink } from "@src/utils/linkSheet";

const PREFIX = "OnboardingStoreInfo";

const classes = {
  root: `${PREFIX}-root`,
  selected: `${PREFIX}-selected`,
};

const StyledStack = styled(Stack)(({ theme }) => ({
  [`& .${classes.root}`]: {
    borderRadius: "8px",
    // alignContent: "center",
    padding: "24px",
    border: `2px solid ${theme.igBorders.core.default}`,
    height: "162px",
    "&:hover, &:focus": {
      border: `2px solid ${theme.igBorders.accent.hover}`,
    },
  },
  [`& .${classes.selected}`]: {
    borderRadius: "8px",
    backgroundColor: `${theme.igBackgroundColors.accent} !important`,
    // alignContent: "center",
    padding: "24px",
    border: `2px solid ${theme.igBorders.accent.default}`,
    height: "162px",
  },
}));

const storeTypes = [
  {
    type: "standard",
    title: "Standard Merchant Store",
    description:
      "A typical Shopify store selling products directly to customers",
  },
  {
    type: "development",
    title: "Development/Testing Store",
    description: "A store used for development or testing purposes",
  },
  {
    type: "headless",
    title: "Headless Store",
    description: "A custom frontend implementation using Shopify as a backend",
    hasTooltip: true,
    tooltipText:
      "A headless store uses a custom front-end not built on Shopify's templates. If you're not sure, your store is probably a standard store.",
  },
];

const orderVolumes: { value: string; label: string }[] = [
  { value: "<500 orders", label: "Less than 500 orders" },
  { value: "500-2,000 orders", label: "500-2,000 orders" },
  { value: "2,000-5,000 orders", label: "2,000-5,000 orders" },
  { value: "5,000-20,000 orders", label: "5,000-20,000 orders" },
  { value: "20,000-100,000 orders", label: "20,000-100,000 orders" },
  { value: "100,000+ orders", label: "More than 100,000 orders" },
  { value: "I don't know", label: "I don't know" },
];

interface OnboardingStoreInfoProps {
  data: Pick<
    OnboardingInfoFe,
    | "storeType"
    | "userReportedOrderCount"
    | "productionStoreUrl"
    | "preferredName"
    | "preferredEmail"
  >;
  onChange: (field: keyof OnboardingInfoFe, value: string) => void;
  storeUrl: string;
}

export const OnboardingStoreInfo = ({
  data,
  onChange,
  storeUrl,
}: OnboardingStoreInfoProps) => {
  const theme = useTheme();
  const [hasModifiedOrderVolume, setHasModifiedOrderVolume] = useImmer(false);
  const handleOrderVolumeChange = (value: string) => {
    setHasModifiedOrderVolume(true);
    onChange("userReportedOrderCount", value);
  };
  const nameSplit = data.preferredName.split(" ");

  const scheduleCallLink = `${plusOnboardingBookDemoLink}?firstname=${nameSplit[0]}&lastname=${nameSplit.length > 0 ? nameSplit[1] : nameSplit[0]}&email=${data.preferredEmail}&domain=${storeUrl}&self_reported_monthly_orders_buckets=${data.userReportedOrderCount}`;
  useViewPage(`Onboarding - ${getMixpanelStepTitle(1)}`);
  return (
    <StyledStack
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor: "transparent !important",
        },
      }}
      direction={"column"}
      spacing={6}
      width={"100%"}
    >
      <Stack direction={"column"} spacing={2}>
        <IgTypography variant={"h1"} fontWeight={"600"}>
          Your store
        </IgTypography>

        <IgTypography color={theme.palette.text.secondary}>
          Tell us about your Shopify store so we can better understand your
          needs.
        </IgTypography>
      </Stack>
      <Stack direction={"column"} spacing={4}>
        <Stack direction={"column"} spacing={4}>
          <IgTypography>
            Which one of these describes your store best?
            <IgTypography
              // variant={"body2"}
              color={theme.palette.text.tonal.error}
            >
              {" "}
              *
            </IgTypography>
          </IgTypography>
          <Grid container columnSpacing={4}>
            {storeTypes.map((store) => (
              <Grid key={store.type} className="relative" size={4}>
                <ListItemButton
                  classes={{ root: classes.root, selected: classes.selected }}
                  selected={data.storeType === store.type}
                  onClick={() => onChange("storeType", store.type)}
                >
                  <Stack
                    // wrap={"nowrap"}
                    spacing={2}
                    textAlign={"start"}
                    alignContent={"start"}
                    // container
                    direction={"column"}
                    sx={{ height: "100%" }}
                  >
                    <Grid
                      container
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"start"}
                      sx={{ height: "100%", paddingTop: "0px !important" }}
                      size={12}
                    >
                      <Grid>
                        <IgTypography variant={"h3"}>
                          {store.title}
                        </IgTypography>
                      </Grid>
                      {store.hasTooltip && (
                        <Grid>
                          <IgTooltip title={store.tooltipText}>
                            <CircleHelp size={20} />
                          </IgTooltip>
                        </Grid>
                      )}
                    </Grid>

                    <IgTypography color={theme.palette.text.secondary}>
                      {store.description}
                    </IgTypography>
                  </Stack>
                </ListItemButton>
              </Grid>
            ))}
          </Grid>
        </Stack>

        {data.storeType === "standard" && (
          <Grid container rowSpacing={2}>
            <Grid size={12}>
              <IgSelect
                fullWidth
                label="How many orders does your store process monthly?"
                value={data.userReportedOrderCount ?? "ig-empty"}
                placeholder="Select your approximate monthly order volume"
                onChange={(e) => handleOrderVolumeChange(e.target.value)}
                required
              >
                {orderVolumes.map((orderVolumeGroup, index) => (
                  <MenuItem key={index} value={orderVolumeGroup.value}>
                    {orderVolumeGroup.label}
                  </MenuItem>
                ))}
              </IgSelect>
            </Grid>
            {!hasModifiedOrderVolume && (
              <Grid size={12}>
                <IgTypography color={theme.palette.text.accent}>
                  We've automatically selected this based on your store's data.
                  Feel free to adjust if needed.
                </IgTypography>
              </Grid>
            )}
          </Grid>
        )}

        {data.storeType === "headless" && (
          <Grid
            mt={6}
            justifyContent={"space-between"}
            alignItems={"center"}
            wrap={"nowrap"}
            container
            direction={"row"}
            sx={{
              backgroundColor: theme.igBackgroundColors.accent,
              padding: "24px",
              borderRadius: "8px",
            }}
          >
            <Grid size={8}>
              <Stack direction={"column"} spacing={1}>
                <IgTypography color={theme.palette.text.accent} variant={"h3"}>
                  We offer specialized solutions for headless stores
                </IgTypography>

                <IgTypography color={theme.palette.text.secondary}>
                  Schedule a call with our team to learn about our unique
                  offerings for headless stores.
                </IgTypography>
              </Stack>
            </Grid>
            <Grid>
              <IgButton onClick={() => window.open(scheduleCallLink, "_blank")}>
                Schedule a Call
              </IgButton>
            </Grid>
          </Grid>
        )}

        {data.storeType === "development" && (
          <Stack direction={"column"} spacing={4} sx={{ width: "100%" }}>
            <IgTextField
              fullWidth={true}
              label="What's the URL of your production store?"
              placeholder="https://your-store.myshopify.com"
              value={data.productionStoreUrl || ""}
              onChange={(e) => onChange("productionStoreUrl", e.target.value)}
              required
            />
            <IgSelect
              fullWidth={true}
              label="How many orders does your production store process monthly?"
              value={data.userReportedOrderCount ?? "ig-empty"}
              placeholder="Select your approximate monthly order volume"
              onChange={(e) =>
                onChange("userReportedOrderCount", e.target.value)
              }
              required
            >
              {orderVolumes.map((orderVolumeGroup, index) => (
                <MenuItem key={index} value={orderVolumeGroup.value}>
                  {orderVolumeGroup.label}
                </MenuItem>
              ))}
            </IgSelect>
          </Stack>
        )}
      </Stack>
    </StyledStack>
  );
};
